/**
 * Generic boot helper for per-portal entry points.
 * Mirrors the recovery semantics of the root src/main.tsx but isolated per portal.
 */
import { createRoot } from "react-dom/client";
import type { ReactElement } from "react";
import "@/index.css";

const BOOT_RECOVERY_KEY = "__app_boot_recovered_once__";

const isRecoverable = (msg: string) => {
  const m = msg.toLowerCase();
  return (
    m.includes("chunkloaderror") ||
    m.includes("loading chunk") ||
    m.includes("failed to fetch dynamically imported module") ||
    m.includes("importing a module script failed") ||
    m.includes("dynamically imported module")
  );
};

const getFlag = () => {
  try { return sessionStorage.getItem(BOOT_RECOVERY_KEY) === "1"; } catch { return false; }
};
const setFlag = (v: boolean) => {
  try {
    if (v) sessionStorage.setItem(BOOT_RECOVERY_KEY, "1");
    else sessionStorage.removeItem(BOOT_RECOVERY_KEY);
  } catch { /* ignore */ }
};

const recover = (msg: string): boolean => {
  if (!isRecoverable(msg)) return false;
  if (!getFlag()) {
    setFlag(true);
    window.location.reload();
    return true;
  }
  return false;
};

export function bootstrapPortal(portal: string, app: ReactElement) {
  window.addEventListener("error", (event) => {
    const msg = event.error?.message ?? String(event.message ?? "");
    if (recover(msg)) event.preventDefault();
  });
  window.addEventListener("unhandledrejection", (event) => {
    const msg = (event.reason as Error)?.message ?? String(event.reason ?? "");
    if (recover(msg)) event.preventDefault();
  });

  const rootEl = document.getElementById("root");
  if (!rootEl) throw new Error(`[${portal}] Root element #root not found`);
  createRoot(rootEl).render(app);

  window.setTimeout(() => setFlag(false), 4000);
}
